import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { history, redo, undo } from '@tiptap/pm/history';
import { gapCursor } from '@tiptap/pm/gapcursor';
import { looksLikeMarkdown } from '../../markdown/wikiMarkdown';
import { EDITOR_COPY } from '../editorCopy';
import {
    applyBlockKind, applyLinkBar, BLOCK_KINDS, createTable, insertBlocks, LIST_KINDS, linkContext,
    MARK_KINDS, toCommand, toggleInlineCode, toggleMarkKind, wikiBackspace, wikiEnter, wikiShiftEnter, wikiTab, ZWSP_RE,
} from './commands';
import { applyTypedRules, createInputRulesPlugin } from './inputRules';
import { markdownToSlice } from './markdownIO';
import { WIKI_SCHEMA_EXTENSIONS, WikiImage, isCellNode } from './schema';
import { createSlashPlugin } from './slash';

/**
 * The Wiki editor's extension list: the custom schema (./schema) plus two
 * behaviour extensions — WikiSlash (the slash menu, highest priority so it
 * sees keys first) and WikiEditing (the prototype's keymap, input rules,
 * paste handling, placeholders, checklist toggle, undo history and gap
 * cursor). Nothing from StarterKit is used, so none of TipTap's default
 * shortcuts (Mod-Shift-7/8/9, Mod-Shift-B, Mod-Alt-C, Mod-Alt-4–6, Mod-U),
 * input rules or nesting behaviours exist (C1-editor.md §3). TipTap's core
 * keymap stays underneath as the fallback (splitBlock, joinBackward, …).
 */

/**
 * Run one toolbar / slash / shortcut command on a view (prototype `wkWysiCmd`).
 * `hooks.openLink(ctx)` opens the link/image/video bar (React state).
 */
export function runWikiCommand(view, kind, hooks = {}) {
    if (!view) return false;
    const { state } = view;
    const { schema } = state;
    let done = false;
    if (BLOCK_KINDS.includes(kind) || LIST_KINDS.includes(kind)) {
        done = toCommand((tr) => applyBlockKind(tr, kind))(state, view.dispatch);
    } else if (MARK_KINDS[kind]) {
        done = toggleMarkKind(kind)(state, view.dispatch);
    } else if (kind === 'code') {
        done = toCommand(toggleInlineCode)(state, view.dispatch);
    } else if (kind === 'hr') {
        done = toCommand((tr) => insertBlocks(tr, [schema.nodes.horizontalRule.create()]))(state, view.dispatch);
    } else if (kind === 'table') {
        done = toCommand((tr) => insertBlocks(tr, [createTable(schema)], { selectFirstHeader: true }))(state, view.dispatch);
    } else if (kind === 'link' || kind === 'image' || kind === 'video') {
        if (typeof hooks.openLink === 'function') hooks.openLink(linkContext(state, kind));
        return true;
    }
    if (!view.hasFocus()) view.focus();
    return done;
}

/** Apply the link bar to a view (prototype `wkLinkApply`), then refocus the surface. */
export function applyLinkToView(view, ctx, url) {
    if (!view) return false;
    const done = toCommand((tr) => applyLinkBar(tr, ctx, url))(view.state, view.dispatch);
    view.focus();
    return done;
}

/* ------------------------------------------------------------ placeholders */

export const wikiPlaceholderKey = new PluginKey('wikiPlaceholder');

/**
 * Prototype `wkWysiMarks`: on every empty top-level paragraph/heading —
 * the intro line when it is the only block, "Heading" in an empty heading,
 * "Write here, or type / for blocks" right after a heading.
 */
export function placeholderFor(doc, index) {
    const node = doc.child(index);
    const name = node.type.name;
    if ((name !== 'paragraph' && name !== 'heading') || node.textContent.replace(ZWSP_RE, '').trim()) return '';
    if (doc.childCount === 1) return EDITOR_COPY.phIntro;
    if (name === 'heading') return EDITOR_COPY.phHeading;
    const prev = index > 0 ? doc.child(index - 1) : null;
    if (prev && prev.type.name === 'heading') return EDITOR_COPY.phAfterHeading;
    return '';
}

function createPlaceholderPlugin() {
    return new Plugin({
        key: wikiPlaceholderKey,
        props: {
            decorations(state) {
                const { doc } = state;
                const decos = [];
                doc.forEach((node, offset, index) => {
                    const ph = placeholderFor(doc, index);
                    if (ph) decos.push(Decoration.node(offset, offset + node.nodeSize, { 'data-ph': ph, class: 'wk-ed-ph' }));
                });
                return DecorationSet.create(doc, decos);
            },
        },
    });
}

/* ----------------------------------------------------------- checklist */

/** Mousedown within 22px of a checklist item's left edge toggles it (prototype `wkWysiDownH`). */
export function toggleTaskAtDom(view, li) {
    let pos;
    try {
        pos = view.posAtDOM(li, 0);
    } catch {
        return false;
    }
    const $p = view.state.doc.resolve(pos);
    for (let d = $p.depth; d > 0; d -= 1) {
        const node = $p.node(d);
        if (node.type.name === 'taskItem') {
            view.dispatch(view.state.tr.setNodeMarkup($p.before(d), null, { ...node.attrs, checked: !node.attrs.checked }));
            return true;
        }
    }
    return false;
}

function createChecklistPlugin() {
    return new Plugin({
        key: new PluginKey('wikiChecklist'),
        props: {
            handleDOMEvents: {
                mousedown(view, event) {
                    const li = event.target && event.target.closest ? event.target.closest('li') : null;
                    if (!li || !view.dom.contains(li)) return false;
                    const list = li.parentNode;
                    if (!list || !list.getAttribute || list.getAttribute('data-task') !== '1') return false;
                    if (event.clientX - li.getBoundingClientRect().left >= 22) return false;
                    event.preventDefault();
                    return toggleTaskAtDom(view, li);
                },
            },
        },
    });
}

/* ---------------------------------------------------------------- paste */

/**
 * Prototype `wkWysiPasteH`: only text/plain is used (HTML on the clipboard is
 * ignored). In code blocks and table cells it goes in as raw text; Markdown-
 * looking text is converted through the engine; anything else is inserted as
 * text and the bulk typing rules run over it.
 */
export function handleWikiPaste(view, event) {
    const cd = event && event.clipboardData;
    const text = cd ? cd.getData('text/plain') : '';
    if (!text) return false;
    const { state } = view;
    const { $from } = state.selection;
    const parent = $from.parent;
    const tr = state.tr;
    if (parent.type.spec.code) {
        tr.insertText(text);
    } else if (isCellNode(parent)) {
        tr.insertText(text.replace(/\r\n?|\n/g, ' '));
    } else if (looksLikeMarkdown(text)) {
        tr.replaceSelection(markdownToSlice(text, state.schema));
    } else {
        tr.insertText(text);
        applyTypedRules(tr, text);
    }
    view.dispatch(tr.scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste'));
    return true;
}

/* -------------------------------------------------------- the extension */

/**
 * `runCommand(kind)` (toolbar/slash/shortcuts) and `openLink(ctx)` come from
 * the editor component; tests may leave them out (then commands run directly
 * and the link shortcuts are no-ops).
 */
export const WikiEditing = Extension.create({
    name: 'wikiEditing',
    priority: 1000,
    addOptions() {
        return { runCommand: null, onEscape: null };
    },
    addKeyboardShortcuts() {
        const view = () => this.editor.view;
        const run = (fn) => () => fn(this.editor.state, this.editor.view.dispatch, this.editor.view);
        const cmd = (kind) => () => {
            if (typeof this.options.runCommand === 'function') return this.options.runCommand(kind) !== false;
            return runWikiCommand(view(), kind);
        };
        return {
            Enter: run(wikiEnter),
            'Shift-Enter': run(wikiShiftEnter),
            Backspace: run(wikiBackspace),
            Tab: run(wikiTab(1)),
            'Shift-Tab': run(wikiTab(-1)),
            'Mod-b': cmd('bold'),
            'Mod-i': cmd('italic'),
            'Mod-Shift-x': cmd('strike'),
            'Mod-e': cmd('code'),
            'Mod-Shift-e': cmd('code'),
            // No underline exists: the shortcut is swallowed.
            'Mod-u': () => true,
            'Mod-Shift-u': () => true,
            'Mod-k': cmd('link'),
            'Mod-Alt-0': cmd('p'),
            'Mod-Alt-1': cmd('h1'),
            'Mod-Alt-2': cmd('h2'),
            'Mod-Alt-3': cmd('h3'),
            // Accessibility (WCAG 2.1.2): Tab never leaves the surface (prototype),
            // so Escape does. An open slash menu handles Escape first (WikiSlash
            // runs at a higher priority); the link bar's input is outside the surface.
            Escape: () => {
                if (typeof this.options.onEscape !== 'function') return false;
                this.options.onEscape();
                return true;
            },
            'Mod-z': run(undo),
            'Mod-Shift-z': run(redo),
            'Mod-y': run(redo),
        };
    },
    addProseMirrorPlugins() {
        return [
            createInputRulesPlugin(),
            createPlaceholderPlugin(),
            createChecklistPlugin(),
            new Plugin({ key: new PluginKey('wikiPaste'), props: { handlePaste: (v, e) => handleWikiPaste(v, e) } }),
            history(),
            gapCursor(),
        ];
    },
});

/**
 * The slash menu runs BEFORE the keymap above (higher priority), so Enter /
 * Tab / arrows / Escape go to the open menu first (prototype `wkWysiKeyH`).
 */
export const WikiSlash = Extension.create({
    name: 'wikiSlash',
    priority: 1100,
    addOptions() {
        return { runCommand: null };
    },
    addProseMirrorPlugins() {
        const runCommand = (pmView, kind) => {
            if (typeof this.options.runCommand === 'function') return this.options.runCommand(kind);
            return runWikiCommand(pmView, kind);
        };
        return [createSlashPlugin({ runCommand })];
    },
});

/**
 * The full extension list. `resolveAsset(id) → Promise<url|null>` shows
 * `/api/wiki/assets/<id>` images through presigned URLs; `runCommand(kind)`
 * routes commands through the component (link bar etc.); `onEscape()` moves
 * focus out of the surface (the keyboard exit).
 */
export function buildWikiEditorExtensions({ resolveAsset = null, runCommand = null, onEscape = null } = {}) {
    return [
        ...WIKI_SCHEMA_EXTENSIONS.map((ext) => (ext === WikiImage ? WikiImage.configure({ resolveAsset }) : ext)),
        WikiSlash.configure({ runCommand }),
        WikiEditing.configure({ runCommand, onEscape }),
    ];
}
